/**
 * Centralized BFF logger: structured JSON lines, in one module so replacing it later touches one file.
 *
 * **Never log PII** — no CPF, email or token, only ids, paths and status codes. `info` is off by
 * default in production, so anything logged at that level will not appear there.
 */

type LogLevel = "error" | "warn" | "info";

export type FailureCategory =
  | "validation"
  | "timeout"
  | "network"
  | "invalid_response"
  | "external_error";

interface LogEntry {
  level: LogLevel;
  context: string;
  message: string;
  path?: string;
  status?: number;
  code?: string;
  details?: unknown;
  requestId?: string;
  durationMs?: number;
  failureCategory?: FailureCategory;
  timestamp: string;
}

function emit(entry: LogEntry): void {
  try {
    const line = JSON.stringify(entry);
    if (entry.level === "error") {
      console.error(line);
    } else if (entry.level === "warn") {
      console.warn(line);
    } else {
      console.log(line);
    }
  } catch {
    // Logging is best-effort and must never alter the caller's control flow.
  }
}

export const logger = {
  error(context: string, message: string, meta?: Omit<LogEntry, "level" | "context" | "message" | "timestamp">) {
    emit({ level: "error", context, message, ...meta, timestamp: new Date().toISOString() });
  },

  warn(context: string, message: string, meta?: Omit<LogEntry, "level" | "context" | "message" | "timestamp">) {
    emit({ level: "warn", context, message, ...meta, timestamp: new Date().toISOString() });
  },

  info(context: string, message: string, meta?: Omit<LogEntry, "level" | "context" | "message" | "timestamp">) {
    emit({ level: "info", context, message, ...meta, timestamp: new Date().toISOString() });
  },
};
